// Login-configuratie. De backend levert via GET /api/auth/config welke provider
// actief is (B2C = KBS-klantlogin, Entra = Universal-beheer) zodat de SPA-build
// geen B2C-instellingen hoeft mee te bakken. VITE_*-vars zijn alleen fallback.
import type { Configuration } from "@azure/msal-browser";

export interface AuthConfig {
  skip_auth: boolean;
  provider: "b2c" | "entra" | "both" | string;
  b2c: { tenant: string; client_id: string; user_flow: string; authority: string } | null;
  entra: { tenant_id: string; client_id: string; api_audience: string } | null;
}

// Build-time hint; de backend-waarde (/auth/config) is leidend.
export const skipAuthHint = import.meta.env.VITE_SKIP_AUTH === "true";

export function fallbackAuthConfig(): AuthConfig {
  const tenant = import.meta.env.VITE_B2C_TENANT || "";
  const clientId = import.meta.env.VITE_B2C_CLIENT_ID || "";
  const flow = import.meta.env.VITE_B2C_USER_FLOW || "b2c_1_signin-flow";
  return {
    skip_auth: skipAuthHint,
    provider: import.meta.env.VITE_AUTH_PROVIDER || "b2c",
    b2c:
      tenant && clientId
        ? {
            tenant,
            client_id: clientId,
            user_flow: flow,
            authority: `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/${flow}`,
          }
        : null,
    entra:
      import.meta.env.VITE_ENTRA_TENANT_ID && import.meta.env.VITE_ENTRA_CLIENT_ID
        ? {
            tenant_id: import.meta.env.VITE_ENTRA_TENANT_ID,
            client_id: import.meta.env.VITE_ENTRA_CLIENT_ID,
            api_audience: import.meta.env.VITE_ENTRA_API_AUDIENCE || "",
          }
        : null,
  };
}

export type ActiveProvider = { kind: "b2c" | "entra"; msal: Configuration; scopes: string[] };

/** Kies de MSAL-configuratie: B2C heeft voorrang (klantlogin). */
export function pickProvider(cfg: AuthConfig): ActiveProvider | null {
  if (cfg.b2c && (cfg.provider === "b2c" || cfg.provider === "both")) {
    return {
      kind: "b2c",
      msal: {
        auth: {
          clientId: cfg.b2c.client_id,
          authority: cfg.b2c.authority,
          knownAuthorities: [`${cfg.b2c.tenant}.b2clogin.com`],
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
          navigateToLoginRequestUrl: true,
        },
        cache: { cacheLocation: "sessionStorage" },
      },
      // B2C: eigen client-id als scope levert een access-token met aud=client-id,
      // precies wat de backend valideert (openid/offline_access voegt MSAL toe).
      scopes: ["openid", "offline_access", cfg.b2c.client_id],
    };
  }
  if (cfg.entra && (cfg.provider === "entra" || cfg.provider === "both")) {
    const aud = cfg.entra.api_audience;
    return {
      kind: "entra",
      msal: {
        auth: {
          clientId: cfg.entra.client_id,
          authority: `https://login.microsoftonline.com/${cfg.entra.tenant_id}`,
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
        },
        cache: { cacheLocation: "sessionStorage" },
      },
      scopes: [aud ? `${aud}/.default` : cfg.entra.client_id],
    };
  }
  return null;
}
